import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Container, Section, Text10, Text12, Text14, Text16, Wrapper } from '../../atoms';
import { Intro, Loading } from '../../molecules';

const SEND_REPORT = gql`
  mutation (
    $description: String!, $location: String, $occurredAt: String, $accused: String,
    $keepContact: Boolean!, $postalAddress: String, $email: String,
    $additionalInfo: String, $attachment: Upload, $website: String
  ) {
    customWhistleblowingSendEmail(
      description: $description, location: $location, occurredAt: $occurredAt, accused: $accused,
      keepContact: $keepContact, postalAddress: $postalAddress, email: $email,
      additionalInfo: $additionalInfo, attachment: $attachment, website: $website
    )
  }
`;

const LIMITS = {
  description: 3000,
  location: 1000,
  accused: 1000,
  additionalInfo: 5000
};

/**
 * Canal de Denúncias, Lei 93/2021.
 *
 * A report may be anonymous, so the contact fields only appear if the reporter
 * asks for them. Nothing is stored anywhere: if the send fails the form says so
 * and keeps everything the reporter typed, because there is no saved copy to
 * recover it from.
 */
export default function WhistleblowingForm({
  title,
  descriptionLabel,
  descriptionHint,
  locationLabel,
  locationHint,
  occurredAtLabel,
  accusedLabel,
  attachmentLabel,
  attachmentPlaceholder,
  attachmentEmptyLabel,
  keepContactLabel,
  keepContactText,
  keepContactYesLabel,
  keepContactNoLabel,
  contactIntro,
  postalAddressLabel,
  emailLabel,
  additionalInfoLabel,
  additionalInfoHint,
  sendLabel,
  infoText,
  successText,
  errorText,
  contactRequiredText,
}) {
  const [inputs, setInputs] = useState({});
  const [keepContact, setKeepContact] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [missContact, setMissContact] = useState(false);

  const [website, setWebsite] = useState('');

  const fileInput = useRef(null);

  const handleInput = (event) => {
    event.persist();
    setInputs((prevInputs) => ({ ...prevInputs, [event.target.name]: event.target.value }));
  };

  const handleFile = (event) => {
    const file = event.target.files && event.target.files[0];
    setAttachment(file || null);
  };

  const reset = () => {
    setInputs({});
    setAttachment(null);
    if (fileInput.current) {
      fileInput.current.value = '';
    }
    setKeepContact(false);
    setMissContact(false);
  };

  const [sendReport, { loading }] = useMutation(SEND_REPORT, {
    onError: () => {
      // the report is not stored anywhere, so never clear the form on failure
      setSuccess(false);
      setError(true);
    },
    onCompleted: (data) => {
      if (data.customWhistleblowingSendEmail === true) {
        reset();
        setSuccess(true);
        setError(false);
      } else {
        setSuccess(false);
        setError(true);
      }
    }
  });

  const submitForm = (e) => {
    e.preventDefault();

    // one way of getting back in touch is enough, but there has to be one
    if (keepContact && !inputs.postalAddress && !inputs.email) {
      setMissContact(true);
      return;
    }

    setMissContact(false);
    setSuccess(false);
    setError(false);

    sendReport({
      variables: {
        description: inputs.description,
        location: inputs.location || '',
        occurredAt: inputs.occurredAt || '',
        accused: inputs.accused || '',
        keepContact,
        postalAddress: keepContact ? (inputs.postalAddress || '') : '',
        email: keepContact ? (inputs.email || '') : '',
        additionalInfo: inputs.additionalInfo || '',
        attachment,
        website
      }
    });
  };

  const counter = (name) => `${(inputs[name] || '').length} / ${LIMITS[name]}`;

  return (
    <Section backgroundColor="#fff">
      <Wrapper both noHeight>
        <Container lg>
          <Content>
            {title &&
              <Header>
                <Intro
                  centerContainer
                  titleSize="H3"
                  title={title}
                  titleColor="#35383B"
                  titleTextAlign="center"
                  titleMaxWidth="640px"
                />
              </Header>
            }
            <Form onSubmit={submitForm}>
              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: descriptionLabel }} />
                {descriptionHint &&
                  <Hint color="#B2B6B9" dangerouslySetInnerHTML={{ __html: descriptionHint }} />
                }
                <Margin />
                <Textarea tall name='description' value={inputs.description || ''} maxLength={LIMITS.description} onChange={handleInput} required />
                <Counter color="#B2B6B9">{counter('description')}</Counter>
              </Field>

              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: locationLabel }} />
                {locationHint &&
                  <Hint color="#B2B6B9" dangerouslySetInnerHTML={{ __html: locationHint }} />
                }
                <Margin />
                <Textarea name='location' value={inputs.location || ''} maxLength={LIMITS.location} onChange={handleInput} />
                <Counter color="#B2B6B9">{counter('location')}</Counter>
              </Field>

              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: occurredAtLabel }} />
                <Margin />
                <Input type='date' name='occurredAt' value={inputs.occurredAt || ''} onChange={handleInput} />
              </Field>

              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: accusedLabel }} />
                <Margin />
                <Textarea name='accused' value={inputs.accused || ''} maxLength={LIMITS.accused} onChange={handleInput} />
                <Counter color="#B2B6B9">{counter('accused')}</Counter>
              </Field>

              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: attachmentLabel }} />
                <Margin />
                <FileRow>
                  <FileButton type='button' onClick={() => fileInput.current && fileInput.current.click()}>
                    <Text14 color="#5D6368" dangerouslySetInnerHTML={{ __html: attachmentPlaceholder }} />
                  </FileButton>
                  <FileName color="#5D6368" title={attachment ? attachment.name : ''}>
                    {attachment ? attachment.name : <span dangerouslySetInnerHTML={{ __html: attachmentEmptyLabel }} />}
                  </FileName>
                </FileRow>
                <HiddenFile ref={fileInput} type='file' name='attachment' onChange={handleFile} />
              </Field>

              <Margin margin={3} />

              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: keepContactLabel }} />
                {keepContactText &&
                  <>
                    <Margin />
                    <Hint color="#5D6368" dangerouslySetInnerHTML={{ __html: keepContactText }} />
                  </>
                }
                <Margin />
                <RadioRow>
                  <RadioLabel>
                    <input
                      type='radio'
                      name='keepContact'
                      checked={keepContact === true}
                      onChange={() => setKeepContact(true)}
                    />
                    <Text14 color="#35383B" dangerouslySetInnerHTML={{ __html: keepContactYesLabel || 'Sim' }} />
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type='radio'
                      name='keepContact'
                      checked={keepContact === false}
                      onChange={() => { setKeepContact(false); setMissContact(false); }}
                    />
                    <Text14 color="#35383B" dangerouslySetInnerHTML={{ __html: keepContactNoLabel || 'Não' }} />
                  </RadioLabel>
                </RadioRow>
              </Field>

              {keepContact &&
                <>
                  {contactIntro &&
                    <>
                      <Margin margin={2} />
                      <Hint color="#5D6368" dangerouslySetInnerHTML={{ __html: contactIntro }} />
                    </>
                  }
                  <Field>
                    <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: postalAddressLabel }} />
                    <Margin />
                    <Input type='text' name='postalAddress' value={inputs.postalAddress || ''} onChange={handleInput} />
                  </Field>
                  <Field>
                    <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: emailLabel }} />
                    <Margin />
                    <Input type='email' name='email' value={inputs.email || ''} onChange={handleInput} />
                  </Field>
                  {missContact &&
                    <>
                      <Margin />
                      <StatusText color="#D14343" dangerouslySetInnerHTML={{ __html: contactRequiredText || 'Indique pelo menos uma forma de contacto.' }} />
                    </>
                  }
                </>
              }

              <Field>
                <Label color="#5D6368" font='InterBold' dangerouslySetInnerHTML={{ __html: additionalInfoLabel }} />
                {additionalInfoHint &&
                  <Hint color="#B2B6B9" dangerouslySetInnerHTML={{ __html: additionalInfoHint }} />
                }
                <Margin />
                <Textarea name='additionalInfo' value={inputs.additionalInfo || ''} maxLength={LIMITS.additionalInfo} onChange={handleInput} />
                <Counter color="#B2B6B9">{counter('additionalInfo')}</Counter>
              </Field>

              <Honeypot
                type='text'
                name='website'
                tabIndex={-1}
                autoComplete='off'
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                aria-hidden='true'
              />

              {infoText &&
                <>
                  <Margin margin={2} />
                  <InfoText color="#B2B6B9" dangerouslySetInnerHTML={{ __html: infoText }} />
                </>
              }

              <Margin margin={4.5} />

              {!loading &&
                <ButtonSubmit color='368D67' type='submit'>
                  <svg>
                    <rect x="0" y="0" fill="none" width="100%" height="100%" />
                  </svg>
                  <Text14 color="#368D67" dangerouslySetInnerHTML={{ __html: sendLabel }} />
                </ButtonSubmit>
              }
              {loading &&
                <LoadingWrap>
                  <Loading small isLoading={loading} />
                </LoadingWrap>
              }

              {success &&
                <>
                  <Margin margin={2} />
                  <StatusText color="#368D67" dangerouslySetInnerHTML={{ __html: successText || 'Denúncia enviada com sucesso.' }} />
                </>
              }
              {error &&
                <>
                  <Margin margin={2} />
                  <StatusText color="#D14343" dangerouslySetInnerHTML={{ __html: errorText || 'Não foi possível enviar a denúncia. Os dados que escreveu foram mantidos, por favor tente novamente.' }} />
                </>
              }
            </Form>
          </Content>
        </Container>
      </Wrapper>
    </Section>
  );
}

const Margin = styled.div`
  margin-top: ${({ margin }) => margin ? margin * 8 : 8}px;
`;
const Content = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
`;
const Header = styled.div`
  width: 100%;
`;
const Form = styled.form`
  width: 560px;
  max-width: 100%;
  margin-top: 36px;
`;
const Field = styled.div`
  margin-top: 24px;
  :first-child{
    margin-top: 0;
  }
`;
const Label = styled(Text10)`
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;
const Hint = styled(Text12)`
  margin-top: 6px;
  font-size: 14px;
  line-height: 20px;
`;
const Counter = styled(Text10)`
  display: block;
  margin-top: 4px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;
const Input = styled.input`
  width: 100%;
  height: 52px;
  background: #EBECED;
  border: 1px solid #EBECED;
  border-radius: 2px;
  padding: 12px 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 17px;
  color: #5D6368;
  font-family: 'InterRegular', sans-serif;
  transition: all 0.4s ease-in-out;
  :hover{
    cursor: pointer;
    border: 1px solid #B2B6B9;
  }
  :focus{
    border: 1px solid #B2B6B9;
  }
`;
const Textarea = styled.textarea`
  width: 100%;
  height: ${({ tall }) => tall ? 200 : 120}px;
  background: #EBECED;
  border: 1px solid #EBECED;
  border-radius: 2px;
  padding: 12px 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 20px;
  color: #5D6368;
  font-family: 'InterRegular', sans-serif;
  transition: all 0.4s ease-in-out;
  resize: vertical;
  :hover{
    cursor: pointer;
    border: 1px solid #B2B6B9;
  }
  :focus{
    border: 1px solid #B2B6B9;
  }
`;
const RadioRow = styled.div`
  display: flex;
  flex-flow: row;
  gap: 32px;
`;
const RadioLabel = styled.label`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 8px;
  :hover{
    cursor: pointer;
  }
  input{
    width: 18px;
    height: 18px;
    accent-color: #368D67;
    :hover{
      cursor: pointer;
    }
  }
`;
const FileRow = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const FileButton = styled.button`
  height: 52px;
  padding: 0 16px;
  background: #EBECED;
  border: 1px solid #B2B6B9;
  border-radius: 2px;
  transition: all 0.4s ease-in-out;
  :hover{
    cursor: pointer;
    border-color: #5D6368;
  }
`;
const FileName = styled(Text14)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const HiddenFile = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;
const Honeypot = styled.input`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
`;
const InfoText = styled(Text12)`
  font-size: 15px;
  line-height: 21px;
  p{
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
  }
  a{
    text-decoration: underline;
    color: #5D6368;
  }
`;
const ButtonSubmit = styled.button`
  margin: 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  height: 52px;
  width: 176px;
  box-sizing: border-box;
  transition: all 0.5s ease-in-out;
  background: transparent;
  border: 0;
  svg {
    height: 52px;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
  rect {
    fill: none;
    stroke: ${({ color }) => color ? '#' + color : '#368D67'};
    stroke-width: 1;
    stroke-dasharray: 422, 0;
    transition: all 0.5s ease-in-out;
  }
  :hover{
    cursor: pointer;
    rect {
      stroke-width: 1;
      stroke-dasharray: 35, 310;
      stroke-dashoffset: 48;
      transition: all 1.35s cubic-bezier(0.19, 1, 0.22, 1);
    }
  }
`;
const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
`;
const StatusText = styled(Text16)`
  text-align: center;
`;
